import { createButton } from "../../components/ui/button";
import { attachOrganizerAccountMenuIfAuthenticated } from "../../components/ui/organizer-account-menu";
import { createStandardSiteAppHeader } from "../../components/ui/site-app-header";
import { createPageContainer } from "../../components/ui/page-container";
import { createSkipLink } from "../../components/ui/skip-link";
import {
  fetchQuizAssignmentTranscript,
  fetchQuizDetail,
  fetchQuizRecipients,
  patchQuizTitle,
  postAssignmentInviteLink,
} from "../../lib/api-quizzes";
import type { Translate } from "../../lib/i18n";
import type { SupportedLocale } from "../../locales/catalog-registry";
import {
  correctIndicesForQuizRow,
  isQuizDetail,
  type QuizDetailPayload,
} from "../organizer-wizard/organizer-wizard";
import {
  isTranscriptPayload,
  organizerTranscriptPageHref,
  renderTranscriptContent,
} from "../organizer-quiz-transcript/transcript-shared";

export interface RenderOrganizerQuizDetailOptions {
  signal: AbortSignal;
  locale: SupportedLocale;
  quizId: string;
}

interface RecipientRosterRow {
  assignment_id: string;
  recipient_email: string;
  invitation_status: string;
  attempt_outcome: string;
  completed_at: string | null;
}

function isRecipientsPayload(
  data: unknown,
): data is { items: RecipientRosterRow[] } {
  if (typeof data !== "object" || data === null) {
    return false;
  }
  const rec = data as Record<string, unknown>;
  if (!Array.isArray(rec.items)) {
    return false;
  }
  for (const row of rec.items) {
    if (typeof row !== "object" || row === null) {
      return false;
    }
    const r = row as Record<string, unknown>;
    if (
      typeof r.assignment_id !== "string" ||
      typeof r.recipient_email !== "string" ||
      typeof r.invitation_status !== "string" ||
      typeof r.attempt_outcome !== "string"
    ) {
      return false;
    }
    const ca = r.completed_at;
    if (ca !== null && ca !== undefined && typeof ca !== "string") {
      return false;
    }
  }
  return true;
}

function formatDetailDate(iso: string, locale: SupportedLocale): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    return iso;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(ms));
}

function invitationLabel(t: Translate, code: string): string {
  if (code === "SENT") {
    return t("quiz.detail.invitation_sent");
  }
  if (code === "NOT_SENT") {
    return t("quiz.detail.invitation_not_sent");
  }
  return code;
}

function attemptLabel(t: Translate, code: string): string {
  if (code === "PASS") {
    return t("quiz.detail.attempt_pass");
  }
  if (code === "FAIL") {
    return t("quiz.detail.attempt_fail");
  }
  if (code === "IN_PROGRESS") {
    return t("quiz.detail.attempt_in_progress");
  }
  if (code === "NOT_SUBMITTED") {
    return t("quiz.detail.attempt_not_submitted");
  }
  return code;
}

function detailTitleDisplayText(detail: QuizDetailPayload, t: Translate): string {
  return detail.title.trim() !== ""
    ? detail.title
    : t("quiz.my_quizzes.untitled_placeholder");
}

/**
 * Inline title editor for **published** quizzes (**PATCH** title only).
 */
function mountPublishedQuizTitleHeading(
  parent: HTMLElement,
  detail: QuizDetailPayload,
  t: Translate,
  options: RenderOrganizerQuizDetailOptions,
): void {
  const h1 = document.createElement("h1");
  h1.className = "quiz-detail__title-heading";

  const feedback = document.createElement("p");
  feedback.className = "quiz-detail__title-feedback";
  feedback.setAttribute("aria-live", "polite");

  const displayRow = document.createElement("div");
  displayRow.className = "quiz-detail__title-display";

  const titleOpenBtn = document.createElement("button");
  titleOpenBtn.type = "button";
  titleOpenBtn.className = "quiz-detail__title-open";
  titleOpenBtn.textContent = detailTitleDisplayText(detail, t);

  const pencilBtn = document.createElement("button");
  pencilBtn.type = "button";
  pencilBtn.className = "qz-btn qz-btn--ghost quiz-detail__title-pencil";
  pencilBtn.setAttribute("aria-label", t("quiz.detail.title_edit_aria"));
  pencilBtn.textContent = t("quiz.detail.title_edit_glyph");

  displayRow.append(titleOpenBtn, pencilBtn);

  const editRow = document.createElement("div");
  editRow.className = "quiz-detail__title-edit";
  editRow.hidden = true;

  const input = document.createElement("input");
  input.type = "text";
  input.className = "quiz-detail__title-input";
  input.maxLength = 512;
  input.placeholder = t("quiz.my_quizzes.untitled_placeholder");

  const okBtn = document.createElement("button");
  okBtn.type = "button";
  okBtn.className = "qz-btn qz-btn--primary quiz-detail__title-action";
  okBtn.setAttribute("aria-label", t("quiz.detail.title_confirm_aria"));
  okBtn.textContent = t("quiz.detail.title_confirm_glyph");

  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "qz-btn qz-btn--secondary quiz-detail__title-action";
  cancelBtn.setAttribute("aria-label", t("quiz.detail.title_cancel_aria"));
  cancelBtn.textContent = t("quiz.detail.title_cancel_glyph");

  editRow.append(input, okBtn, cancelBtn);

  h1.append(displayRow, editRow);
  parent.appendChild(h1);
  parent.appendChild(feedback);

  const enterEdit = (): void => {
    feedback.textContent = "";
    input.value =
      detail.title.trim() !== ""
        ? detail.title
        : "";
    displayRow.hidden = true;
    editRow.hidden = false;
    input.focus();
    input.select();
  };

  const exitEdit = (): void => {
    editRow.hidden = true;
    displayRow.hidden = false;
    feedback.textContent = "";
  };

  const syncDisplayLabel = (): void => {
    titleOpenBtn.textContent = detailTitleDisplayText(detail, t);
  };

  titleOpenBtn.addEventListener("click", enterEdit);
  pencilBtn.addEventListener("click", enterEdit);

  cancelBtn.addEventListener("click", () => {
    exitEdit();
  });

  const onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape" && !editRow.hidden) {
      ev.preventDefault();
      exitEdit();
    }
  };
  editRow.addEventListener("keydown", onKeyDown);

  okBtn.addEventListener("click", () => {
    void (async () => {
      const trimmed = input.value.trim();
      okBtn.disabled = true;
      cancelBtn.disabled = true;
      feedback.textContent = "";
      try {
        const res = await patchQuizTitle(options.quizId, trimmed, {
          signal: options.signal,
        });
        if (options.signal.aborted) {
          return;
        }
        if (!res.ok) {
          feedback.textContent = t("quiz.my_quizzes.title_save_failed");
          okBtn.disabled = false;
          cancelBtn.disabled = false;
          return;
        }
        let body: unknown;
        try {
          body = (await res.json()) as unknown;
        } catch {
          feedback.textContent = t("quiz.my_quizzes.title_save_failed");
          okBtn.disabled = false;
          cancelBtn.disabled = false;
          return;
        }
        const titleOut =
          typeof body === "object" &&
          body !== null &&
          typeof (body as { title?: unknown }).title === "string"
            ? (body as { title: string }).title
            : trimmed;
        detail.title = titleOut;
        syncDisplayLabel();
        exitEdit();
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") {
          return;
        }
        feedback.textContent = t("quiz.my_quizzes.title_save_failed");
      } finally {
        okBtn.disabled = false;
        cancelBtn.disabled = false;
      }
    })();
  });
}

/**
 * Read-only organizer **quiz detail** (**DD-SRS-F-TRAINING-QUIZ.35**): **`GET`**
 * **`/quizzes/:id`** plus **`GET`** **`/quizzes/:id/recipients`**.
 *
 * @param mount - Root container cleared by this view.
 * @param t - Localized resolver.
 * @param options - Quiz id, abort signal, and locale for dates.
 */
export async function renderOrganizerQuizDetail(
  mount: HTMLElement,
  t: Translate,
  options: RenderOrganizerQuizDetailOptions,
): Promise<void> {
  mount.replaceChildren();
  mount.className = "";

  const shell = document.createElement("div");
  shell.className = "home-shell";
  mount.appendChild(shell);

  shell.appendChild(
    createSkipLink(t("quiz.home.skip_to_main"), "quiz-detail-main"),
  );

  const page = createPageContainer();
  shell.appendChild(page);

  const { header, authSlot } = createStandardSiteAppHeader(t, options.locale);
  page.appendChild(header);

  void attachOrganizerAccountMenuIfAuthenticated(authSlot, t, {
    signal: options.signal,
  });

  const mainEl = document.createElement("main");
  mainEl.id = "quiz-detail-main";
  mainEl.className = "quiz-detail__main";
  mainEl.tabIndex = -1;
  page.appendChild(mainEl);

  const nav = document.createElement("p");
  const backList = document.createElement("a");
  backList.href = "/quizzes";
  backList.textContent = t("quiz.detail.back_my_quizzes");
  nav.appendChild(backList);

  const status = document.createElement("p");
  status.className = "quiz-detail__status";
  status.textContent = t("quiz.detail.loading");

  mainEl.appendChild(nav);
  mainEl.appendChild(status);

  try {
    const detailRes = await fetchQuizDetail(options.quizId, {
      signal: options.signal,
    });
    if (options.signal.aborted) {
      return;
    }
    if (detailRes.status === 401) {
      status.textContent = t("quiz.detail.sign_in_required");
      const signIn = document.createElement("p");
      const a = document.createElement("a");
      a.href = "/organizer/sign-in";
      a.textContent = t("quiz.organizer.sign_in.home_link");
      signIn.appendChild(a);
      mainEl.appendChild(signIn);
      return;
    }
    if (detailRes.status === 403 || detailRes.status === 404) {
      status.textContent = t("quiz.detail.not_found");
      return;
    }
    if (!detailRes.ok) {
      status.textContent = t("quiz.detail.load_failed");
      return;
    }
    let rawDetail: unknown;
    try {
      rawDetail = (await detailRes.json()) as unknown;
    } catch {
      status.textContent = t("quiz.detail.load_failed");
      return;
    }
    if (!isQuizDetail(rawDetail)) {
      status.textContent = t("quiz.detail.load_failed");
      return;
    }
    const detail = rawDetail;

    const recipRes = await fetchQuizRecipients(options.quizId, {
      signal: options.signal,
    });
    if (options.signal.aborted) {
      return;
    }
    if (!recipRes.ok) {
      status.textContent = t("quiz.detail.recipients_failed");
      return;
    }
    let rawRecip: unknown;
    try {
      rawRecip = (await recipRes.json()) as unknown;
    } catch {
      status.textContent = t("quiz.detail.recipients_failed");
      return;
    }
    if (!isRecipientsPayload(rawRecip)) {
      status.textContent = t("quiz.detail.recipients_failed");
      return;
    }

    status.textContent = "";
    mainEl.replaceChildren();

    const nav2 = document.createElement("p");
    const backList2 = document.createElement("a");
    backList2.href = "/quizzes";
    backList2.textContent = t("quiz.detail.back_my_quizzes");
    nav2.appendChild(backList2);
    mainEl.appendChild(nav2);

    const isPublished =
      detail.status.trim().toLowerCase() === "published";

    if (isPublished) {
      mountPublishedQuizTitleHeading(mainEl, detail, t, options);
    } else {
      const h1 = document.createElement("h1");
      h1.className = "quiz-detail__title";
      h1.textContent = detailTitleDisplayText(detail, t);
      mainEl.appendChild(h1);
    }

    const quizIdLine = document.createElement("p");
    quizIdLine.className = "quiz-detail__quiz-id";
    quizIdLine.textContent = t("quiz.detail.quiz_id_line", {
      quiz_id: detail.quiz_id,
    });
    mainEl.appendChild(quizIdLine);

    const meta = document.createElement("p");
    meta.className = "quiz-detail__meta";
    const created = document.createElement("time");
    created.dateTime = detail.created_at;
    created.textContent = t("quiz.detail.created_line", {
      date: formatDetailDate(detail.created_at, options.locale),
    });
    meta.appendChild(created);
    mainEl.appendChild(meta);

    const statusLine = document.createElement("p");
    statusLine.className = "quiz-detail__muted";
    statusLine.textContent = t("quiz.detail.status_line", {
      status: detail.status,
    });
    mainEl.appendChild(statusLine);

    if (!isPublished) {
      const wizardNav = document.createElement("p");
      const wizardLink = document.createElement("a");
      wizardLink.href = `/quizzes/wizard/${encodeURIComponent(options.quizId)}`;
      wizardLink.textContent = t("quiz.detail.open_wizard_add_recipients");
      wizardNav.appendChild(wizardLink);
      mainEl.appendChild(wizardNav);
    }

    const poolHeading = document.createElement("h2");
    poolHeading.textContent = t("quiz.detail.pool_heading");
    mainEl.appendChild(poolHeading);

    const questions = detail.questions ?? [];
    const activeQs = questions.filter((q) => q.active);
    const inactiveCount = questions.length - activeQs.length;

    const poolSummary = document.createElement("p");
    poolSummary.className = "quiz-detail__muted";
    poolSummary.textContent = t("quiz.detail.pool_summary", {
      active: String(activeQs.length),
      total: String(questions.length),
      inactive: String(inactiveCount),
    });
    mainEl.appendChild(poolSummary);

    if (activeQs.length === 0) {
      const emptyPool = document.createElement("p");
      emptyPool.textContent = t("quiz.detail.pool_empty");
      mainEl.appendChild(emptyPool);
    } else {
      const poolList = document.createElement("ol");
      poolList.className = "quiz-detail__pool-list";
      for (const q of activeQs) {
        const li = document.createElement("li");
        li.className = "quiz-detail__pool-item";
        const stem = document.createElement("p");
        stem.className = "quiz-detail__stem";
        stem.textContent = q.stem;
        li.appendChild(stem);
        const ulCh = document.createElement("ul");
        ulCh.className = "quiz-detail__choices";
        const correctIdxSet = new Set(correctIndicesForQuizRow(q));
        q.choices.forEach((choice, idx) => {
          const liCh = document.createElement("li");
          if (correctIdxSet.has(idx)) {
            liCh.textContent = t("quiz.detail.choice_correct", {
              text: choice.text,
            });
          } else {
            liCh.textContent = choice.text;
          }
          ulCh.appendChild(liCh);
        });
        li.appendChild(ulCh);
        poolList.appendChild(li);
      }
      mainEl.appendChild(poolList);
    }

    const rosterHeading = document.createElement("h2");
    rosterHeading.textContent = t("quiz.detail.roster_heading");
    mainEl.appendChild(rosterHeading);

    if (rawRecip.items.length === 0) {
      const emptyRoster = document.createElement("p");
      emptyRoster.textContent = t("quiz.detail.roster_empty");
      mainEl.appendChild(emptyRoster);
      const hint = document.createElement("p");
      hint.className = "quiz-detail__muted";
      const w2 = document.createElement("a");
      w2.href = `/quizzes/wizard/${encodeURIComponent(options.quizId)}`;
      w2.textContent = t("quiz.detail.roster_empty_hint_wizard");
      hint.appendChild(w2);
      mainEl.appendChild(hint);
    } else {
      const transcriptDialog = document.createElement("dialog");
      transcriptDialog.className = "quiz-detail__transcript-dialog";
      transcriptDialog.setAttribute(
        "aria-labelledby",
        "quiz-detail-transcript-title",
      );
      transcriptDialog.addEventListener("click", (ev) => {
        if (ev.target === transcriptDialog) {
          transcriptDialog.close();
        }
      });
      const transcriptPanel = document.createElement("div");
      transcriptPanel.className = "quiz-detail__transcript-panel";
      const transcriptTitle = document.createElement("h2");
      transcriptTitle.id = "quiz-detail-transcript-title";
      transcriptTitle.className = "quiz-detail__transcript-title";
      transcriptTitle.textContent = t("quiz.detail.transcript_title");
      const transcriptBody = document.createElement("div");
      transcriptBody.className = "quiz-detail__transcript-body";
      const transcriptActions = document.createElement("div");
      transcriptActions.className = "quiz-detail__transcript-actions";
      const fullPageLink = document.createElement("a");
      fullPageLink.className =
        "qz-btn qz-btn--secondary quiz-detail__transcript-fullscreen-link";
      fullPageLink.textContent = t("quiz.detail.transcript_fullscreen");
      fullPageLink.setAttribute(
        "aria-label",
        t("quiz.detail.transcript_fullscreen_aria"),
      );
      const closeTranscript = createButton(t("quiz.detail.transcript_close"), {
        variant: "secondary",
      });
      closeTranscript.addEventListener("click", () => {
        transcriptDialog.close();
      });
      transcriptActions.appendChild(fullPageLink);
      transcriptActions.appendChild(closeTranscript);
      transcriptPanel.appendChild(transcriptTitle);
      transcriptPanel.appendChild(transcriptBody);
      transcriptPanel.appendChild(transcriptActions);
      transcriptDialog.appendChild(transcriptPanel);
      mainEl.appendChild(transcriptDialog);

      const openTranscript = (
        assignmentId: string,
        recipientEmail: string,
      ): void => {
        fullPageLink.href = organizerTranscriptPageHref(
          options.quizId,
          assignmentId,
        );
        void (async () => {
          transcriptTitle.textContent = t("quiz.detail.transcript_title");
          transcriptBody.textContent = t("quiz.detail.transcript_loading");
          transcriptDialog.showModal();
          try {
            const res = await fetchQuizAssignmentTranscript(
              options.quizId,
              assignmentId,
              { signal: options.signal },
            );
            if (options.signal.aborted) {
              return;
            }
            if (!res.ok) {
              transcriptBody.textContent = t("quiz.detail.transcript_error");
              return;
            }
            let raw: unknown;
            try {
              raw = await res.json();
            } catch {
              transcriptBody.textContent = t("quiz.detail.transcript_error");
              return;
            }
            if (!isTranscriptPayload(raw)) {
              transcriptBody.textContent = t("quiz.detail.transcript_error");
              return;
            }
            renderTranscriptContent(
              transcriptBody,
              t,
              options.locale,
              raw,
              recipientEmail,
            );
          } catch {
            if (options.signal.aborted) {
              return;
            }
            transcriptBody.textContent = t("quiz.detail.transcript_error");
          }
        })();
      };

      const wrap = document.createElement("div");
      wrap.className = "quiz-detail__table-wrap";
      const tbl = document.createElement("table");
      tbl.className = "quiz-detail__roster-table";
      const thead = document.createElement("thead");
      const hr = document.createElement("tr");
      for (const key of [
        "quiz.detail.col_email",
        "quiz.detail.col_invitation",
        "quiz.detail.col_attempt",
        "quiz.detail.col_completed",
        "quiz.detail.col_invite",
      ] as const) {
        const th = document.createElement("th");
        th.scope = "col";
        th.textContent = t(key);
        hr.appendChild(th);
      }
      thead.appendChild(hr);
      tbl.appendChild(thead);
      const tbody = document.createElement("tbody");
      for (const row of rawRecip.items) {
        const tr = document.createElement("tr");
        const tdEmail = document.createElement("td");
        tdEmail.textContent = row.recipient_email;
        const tdInv = document.createElement("td");
        tdInv.textContent = invitationLabel(t, row.invitation_status);
        const tdAtt = document.createElement("td");
        if (row.attempt_outcome === "PASS" || row.attempt_outcome === "FAIL") {
          const outcomeBtn = createButton(
            attemptLabel(t, row.attempt_outcome),
            {
              variant: "secondary",
            },
          );
          outcomeBtn.type = "button";
          outcomeBtn.classList.add("quiz-detail__transcript-btn");
          outcomeBtn.setAttribute(
            "aria-label",
            t("quiz.detail.transcript_open_aria", {
              email: row.recipient_email,
            }),
          );
          outcomeBtn.addEventListener("click", () => {
            openTranscript(row.assignment_id, row.recipient_email);
          });
          tdAtt.appendChild(outcomeBtn);
        } else {
          tdAtt.textContent = attemptLabel(t, row.attempt_outcome);
        }
        const tdDone = document.createElement("td");
        if (row.completed_at) {
          tdDone.textContent = formatDetailDate(
            row.completed_at,
            options.locale,
          );
        } else {
          tdDone.textContent = t("quiz.detail.completed_dash");
        }
        const tdInvite = document.createElement("td");
        const inviteBtn = createButton(t("quiz.detail.get_invite_link"), {
          variant: "secondary",
        });
        inviteBtn.classList.add("quiz-detail__roster-invite-btn");
        inviteBtn.addEventListener("click", () => {
          void (async () => {
            if (!window.confirm(t("quiz.detail.invite_link_confirm"))) {
              return;
            }
            inviteBtn.disabled = true;
            try {
              const res = await postAssignmentInviteLink(
                options.quizId,
                row.assignment_id,
                { signal: options.signal },
              );
              if (options.signal.aborted) {
                return;
              }
              if (res.status === 503) {
                let errCode = "";
                try {
                  const eb = (await res.json()) as { error?: unknown };
                  if (typeof eb.error === "string") {
                    errCode = eb.error;
                  }
                } catch {
                  errCode = "";
                }
                if (errCode === "invite_origin_unavailable") {
                  window.alert(t("quiz.detail.invite_link_no_origin"));
                } else {
                  window.alert(t("quiz.detail.invite_link_failed"));
                }
                return;
              }
              if (!res.ok) {
                window.alert(t("quiz.detail.invite_link_failed"));
                return;
              }
              let rawJson: unknown;
              try {
                rawJson = await res.json();
              } catch {
                window.alert(t("quiz.detail.invite_link_failed"));
                return;
              }
              if (
                typeof rawJson !== "object" ||
                rawJson === null ||
                typeof (rawJson as { invite_url?: unknown }).invite_url !==
                  "string"
              ) {
                window.alert(t("quiz.detail.invite_link_failed"));
                return;
              }
              const inviteUrl = (rawJson as { invite_url: string }).invite_url;
              if (inviteUrl === "") {
                window.alert(t("quiz.detail.invite_link_failed"));
                return;
              }
              try {
                await navigator.clipboard.writeText(inviteUrl);
                window.alert(t("quiz.detail.invite_link_copied"));
              } catch {
                window.prompt(
                  t("quiz.detail.invite_link_manual_copy"),
                  inviteUrl,
                );
              }
            } finally {
              inviteBtn.disabled = false;
            }
          })();
        });
        tdInvite.appendChild(inviteBtn);

        tr.append(tdEmail, tdInv, tdAtt, tdDone, tdInvite);
        tbody.appendChild(tr);
      }
      tbl.appendChild(tbody);
      wrap.appendChild(tbl);
      mainEl.appendChild(wrap);
    }
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      return;
    }
    mainEl.replaceChildren();
    const errP = document.createElement("p");
    errP.className = "quiz-detail__status";
    errP.textContent = t("quiz.detail.load_failed");
    mainEl.appendChild(errP);
  }
}
